import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  LoadingSpinner,
  ScrollArea,
  useDialogExitWarning,
} from '@ais-ui/ui-core';
import React, { useCallback, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDrawingContext, useResetDrawingContext } from '@ais-ui/react-openlayers';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { useLocalization } from '@ais-ui/locale-management';
import { useGetShareGroups } from '@ais-ui/websockets';
import { cn } from '@ais-ui/utils';
import { AreaDetectorFormValues, fieldToAreaAccordionMap } from './areaForm';
import { AreaAdvancedForm } from './AreaForm';
import { DetectorFormTypes } from '../Mutations/detectorForm';
import { MutateBaseForm } from '../Mutations/MutateBaseForm';
import { useErrorNavigation } from '../detectorUtils';
import { EventdetectorDefinition } from '@ais-ui/api-spec/eventdetector';
import { useDrawWithViewMode } from '../../../../hooks/useDrawWithViewMode';

interface DetectorDialogProps {
  form: UseFormReturn<AreaDetectorFormValues>;
  onSubmit: (data: AreaDetectorFormValues) => void;
  setShowOuterDialog: (showOuterDialog: boolean) => void;
  mutateType: 'edit' | 'create';
  isSubmitting: boolean;
  detector?: EventdetectorDefinition;
}

export const AreaDetectorDialog = ({
  form,
  onSubmit,
  setShowOuterDialog,
  mutateType,
  isSubmitting,
  detector,
}: DetectorDialogProps) => {
  const { messages } = useLocalization();
  const [showDialog, setShowDialog] = useState<boolean>(true);
  const [showShape, setShowShape] = useState<GeoJson | null>(null);
  const { showDrawPanel } = useDrawingContext();
  const { onStartDrawingFromDialog } = useDrawWithViewMode(form, setShowDialog);
  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();
  const detectorType = 'area';

  const onShowShape = useCallback((shape: GeoJson | undefined) => {
    if (!shape) {
      return;
    }
    setShowDialog(false);
    setShowShape(shape as GeoJson);
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    setShowDialog(isOpen); // Update showDialog state

    // Make sure that we are not unmounting the component when drawing, but we will unmount it if we are not drawing
    if (!showDrawPanel) {
      setShowOuterDialog(isOpen);
    }
  };

  const { openSection, setOpenSection } = useErrorNavigation(
    form,
    fieldToAreaAccordionMap
  );

  const dialogTitle = useMemo(
    () =>
      mutateType === 'create'
        ? messages.eventdetectorsCreateTitle.replace('{label}', detectorType)
        : messages.eventdetectorsEditTitle.replace('{label}', detectorType),
    [mutateType, messages, detectorType]
  );

  const resetDrawingContext = useResetDrawingContext();

  const handleExitConfirm = React.useCallback(() => {
    if (showDrawPanel) {
      resetDrawingContext();
    }
    setShowDialog(false);
    setShowOuterDialog(false);
  }, [showDrawPanel, resetDrawingContext, setShowDialog, setShowOuterDialog]);

  const { handleClose, ExitWarningDialog } = useDialogExitWarning({
    onConfirm: handleExitConfirm,
    title: messages.eventdetectorWarningTitle || 'Discard event detector changes?',
    description: messages.eventdetectorWarningDescription || 'Your event detector configuration will be lost. Are you sure you want to close?',
  });

  return (
    <Form {...form}>
      <Dialog open={showDialog} onOpenChange={handleOpenChange}>
        <DialogContent
          className="pt-12 pl-12 pr-8 !max-w-3xl lg:h-[760px] 2xl:h-[960px] h-[600px]"
          onClose={handleClose}
        >
          <DialogHeader className="h-0 space-y-3">
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>
              {messages.eventdetectorDialogDescription}
            </DialogDescription>
          </DialogHeader>
          {isLoadingGroups && (
            <div className="h-[350px]">
              <LoadingSpinner></LoadingSpinner>
            </div>
          )}

          {!isLoadingGroups && !!groups && (
            <div className={cn('lg:h-[460px] 2xl:h-[670px] h-[300px]')}>
              <Accordion
                type="single"
                collapsible
                defaultValue="detector-item"
                value={openSection}
                onValueChange={setOpenSection}
              >
                <AccordionItem value="detector-item">
                  <AccordionTrigger>
                    <h3 className="text-lg font-medium">Detector</h3>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ScrollArea className="lg:h-[340px] 2xl:h-[530px] h-[250px] w-full py-2 pl-2 pr-4 overflow-hidden">
                      <MutateBaseForm
                        form={form as UseFormReturn<DetectorFormTypes>}
                        detectorType={detectorType}
                        onStartDrawing={onStartDrawingFromDialog}
                        onShowShape={onShowShape}
                        detector={detector}
                        mutateType={mutateType}
                      ></MutateBaseForm>
                    </ScrollArea>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="advanced-item">
                  <AccordionTrigger>
                    <h3 className="text-lg font-medium">Advanced</h3>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ScrollArea className="lg:h-[340px] 2xl:h-[530px] h-[250px] w-full py-2 pl-2 pr-4 overflow-hidden">
                      <AreaAdvancedForm form={form}></AreaAdvancedForm>
                    </ScrollArea>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          )}
          <DialogFooter className="h-[0px] pr-4">
            <Button
              className="mt-4"
              id="submit-area-event-detector-button"
              disabled={isSubmitting}
              onClick={form.handleSubmit(onSubmit)}
              type="submit"
            >
              {isSubmitting
                ? messages.genericSubmitting
                : messages.genericSubmit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ExitWarningDialog />
    </Form>
  );
};
